// ============================================================================
// MERMAID DIAGRAM COMPONENT
// Renders Mermaid diagrams inline using mermaid.js
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import DOMPurify from 'dompurify';
import { AlertCircle, Maximize2, Minimize2 } from 'lucide-react';

interface MermaidDiagramProps {
  code: string;
  theme?: 'default' | 'forest' | 'dark' | 'neutral';
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ code, theme = 'default' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [svgContent, setSvgContent] = useState<string>('');

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current || !code) return;

      try {
        // Initialize mermaid with configuration
        mermaid.initialize({
          startOnLoad: false,
          theme: theme,
          securityLevel: 'strict',
          fontSize: 16,
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis',
          },
          sequence: {
            useMaxWidth: true,
            wrap: true,
          },
          c4: {
            useMaxWidth: true,
          },
        });

        // Generate a unique ID for the diagram
        const id = `mermaid-${Math.random().toString(36).slice(2, 11)}`;

        // Render the diagram
        const { svg } = await mermaid.render(id, code);

        // Sanitize SVG output to prevent XSS
        const cleanSvg = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true } });

        setSvgContent(cleanSvg);
        setError(null);
      } catch (err: any) {
        console.error('Mermaid rendering error:', err);
        setError(err.message || 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [code, theme]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (error) {
    return (
      <div
        style={{
          padding: '16px',
          background: '#fff5f5',
          border: '1px solid #ffc9c9',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}
      >
        <AlertCircle size={20} color="#c92a2a" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#c92a2a', marginBottom: '4px' }}>
            Failed to render Mermaid diagram
          </div>
          <div style={{ fontSize: '13px', color: '#c92a2a' }}>{error}</div>
          <details style={{ marginTop: '8px' }}>
            <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#495057' }}>
              View diagram code
            </summary>
            <pre
              style={{
                marginTop: '8px',
                padding: '8px',
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                fontSize: '11px',
                fontFamily: 'monospace',
                overflow: 'auto',
                maxHeight: '200px',
              }}
            >
              {code}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  const diagramContent = (
    <div
      ref={containerRef}
      style={{
        background: 'white',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '16px',
        overflow: 'auto',
        maxHeight: isFullscreen ? '90vh' : '600px',
        position: 'relative',
      }}
    >
      {/* Fullscreen toggle button */}
      <button
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          padding: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? <Minimize2 size={16} color="#495057" /> : <Maximize2 size={16} color="#495057" />}
      </button>

      {/* Render SVG */}
      {svgContent && (
        <div
          dangerouslySetInnerHTML={{ __html: svgContent }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      )}
    </div>
  );

  if (isFullscreen) {
    return (
      <>
        {/* Fullscreen backdrop */}
        <div
          onClick={toggleFullscreen}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9998,
          }}
        />
        {/* Fullscreen diagram */}
        <div
          style={{
            position: 'fixed',
            top: '5vh',
            left: '5vw',
            right: '5vw',
            bottom: '5vh',
            zIndex: 9999,
          }}
        >
          {diagramContent}
        </div>
      </>
    );
  }

  return diagramContent;
};
